import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React from 'react';
import { ActionSheet } from '@/components/action-sheet';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import { RESTAURANT_EMPLOYEES_QUERY } from '@/hooks/use-restaurant-employees';
import {
  Restaurant_RemoveEmployeeMutation,
  Restaurant_RemoveEmployeeMutationVariables,
} from '@/types';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  businessName: string;
  businessUUID: string;
  employeeName: string;
  employeeUUID: string;
  isVisible: boolean;
  onClose: (employeeRemoved: boolean) => void;
  supplierUUID: string;
}

export const REMOVE_RESTAURANT_EMPLOYEE_MUTATION = gql`
  mutation Restaurant_RemoveEmployee(
    $businessUUID: uuid!
    $employeeUUID: uuid!
    $supplierUUID: uuid!
  ) {
    removeEmployeeFromBusiness(
      business_id: $businessUUID
      employee_id: $employeeUUID
      supplier_id: $supplierUUID
    ) {
      employee_id
    }
  }
`;

export const RemoveEmployeeActionSheet: React.FC<Props> = ({
  businessName,
  businessUUID,
  employeeName,
  employeeUUID,
  isVisible,
  onClose,
  supplierUUID,
}) => {
  const { showErrorToast } = useToasts();

  const [removeEmployee, { loading }] = useMutation<
    Restaurant_RemoveEmployeeMutation,
    Restaurant_RemoveEmployeeMutationVariables
  >(REMOVE_RESTAURANT_EMPLOYEE_MUTATION, {
    refetchQueries: [getOperationName(RESTAURANT_EMPLOYEES_QUERY) as string],
  });

  return (
    <ActionSheet
      actions={[i18n.t('remove'), i18n.t('cancel')]}
      isVisible={isVisible}
      loadingActionIndex={loading ? 0 : null}
      message={i18n.t('restaurant.remove_employee_confirm', {
        businessName,
        employeeName,
      })}
      onAction={async (_, index) => {
        let employeeRemoved = false;
        if (index === 0) {
          Analytics.track('account_restaurant.remove_employee.press', {
            businessUUID,
            employeeUUID,
          });

          try {
            await removeEmployee({
              variables: {
                businessUUID,
                employeeUUID,
                supplierUUID,
              },
            });
            employeeRemoved = true;
          } catch (e) {
            showErrorToast();
          }
        }
        onClose(employeeRemoved);
      }}
      onClose={() => onClose(false)}
      title={i18n.t('restaurant.remove_employee_title', { employeeName })}
    />
  );
};
